import { useEffect, useRef, useState, type RefObject } from 'react';
import { useQueryClient } from '@tanstack/react-query';

// Pull to refresh on phones (the installed app has no browser to do it): with the content at its top, a finger
// dragged down pulls the content and a small pill; past the threshold, releasing refetches the page's data and asks
// the service worker for a new version. No reload: the page, its scroll and its state stay. Touch only; an inner
// scroll area that is not at its top, a horizontal swipe or a text selection leaves the gesture alone.

export const THRESHOLD = 70;
const MAX = 120;
const RESISTANCE = 0.5;

export type PullState = 'idle' | 'pulling' | 'armed' | 'refreshing' | 'done';

/** True when nothing between the touched element and the container is scrolled away from its top */
export function atTop(target: EventTarget | null, container: HTMLElement): boolean {
  if (container.scrollTop > 0) return false;
  for (let el = target instanceof Element ? target : null; el && el !== container; el = el.parentElement) {
    const style = getComputedStyle(el);
    if (/(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight && el.scrollTop > 0) return false;
  }
  return true;
}

export function usePullToRefresh(container: RefObject<HTMLElement | null>) {
  const qc = useQueryClient();
  const [state, setState] = useState<PullState>('idle');
  const [distance, setDistance] = useState(0);
  const gesture = useRef<{ x: number; y: number; pulling: boolean } | null>(null);
  const busy = useRef(false);

  useEffect(() => {
    const el = container.current;
    if (!el || !window.matchMedia?.('(pointer: coarse)').matches) return;

    const reset = () => { gesture.current = null; setDistance(0); setState((s) => (s === 'pulling' || s === 'armed' ? 'idle' : s)); };

    const onStart = (e: TouchEvent) => {
      if (busy.current || e.touches.length !== 1 || !atTop(e.target, el)) return;
      gesture.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, pulling: false };
    };
    const onMove = (e: TouchEvent) => {
      const g = gesture.current;
      if (!g) return;
      const dx = e.touches[0].clientX - g.x;
      const dy = e.touches[0].clientY - g.y;
      if (!g.pulling) {
        if (Math.abs(dy) < 6 && Math.abs(dx) < 6) return;
        // a horizontal swipe, an upward scroll or a selection in progress: not a pull
        if (Math.abs(dx) > Math.abs(dy) || dy < 0 || (window.getSelection()?.toString() ?? '') !== '') { gesture.current = null; return; }
        g.pulling = true;
      }
      e.preventDefault();
      const d = Math.min(MAX, Math.max(0, dy * RESISTANCE));
      setDistance(d);
      setState(d >= THRESHOLD ? 'armed' : 'pulling');
    };
    const onEnd = () => {
      const g = gesture.current;
      if (!g?.pulling) { gesture.current = null; return; }
      gesture.current = null;
      setDistance((d) => {
        if (d < THRESHOLD) { setState('idle'); return 0; }
        busy.current = true;
        setState('refreshing');
        void (async () => {
          try {
            await qc.refetchQueries({ type: 'active' });
            await navigator.serviceWorker?.getRegistration?.().then((r) => r?.update()).catch(() => undefined);
          } finally {
            setState('done');
            window.setTimeout(() => { busy.current = false; setState('idle'); setDistance(0); }, 1000);
          }
        })();
        return THRESHOLD * 0.6;
      });
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', reset);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', reset);
    };
  }, [container, qc]);

  return { state, distance };
}

/** The pill above the content, and the polite announcement of a finished refresh */
export function PullIndicator({ state, distance }: { state: PullState; distance: number }) {
  const progress = Math.min(1, distance / THRESHOLD);
  const visible = state !== 'idle';
  const label = state === 'armed' ? 'Release to refresh' : state === 'done' ? 'Updated' : null;
  return (
    <>
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 z-20 flex justify-center"
        style={{ transform: `translateY(${Math.max(0, distance - 40)}px)`, opacity: visible ? Math.max(progress, state === 'refreshing' || state === 'done' ? 1 : 0) : 0 }}>
        <div className="mt-2 flex items-center gap-2 rounded-full px-2 py-1.5 text-xs"
          style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border-2)', boxShadow: 'var(--shadow-card)', color: 'var(--color-text-2)' }}>
          {state === 'refreshing' ? (
            <span className="ptr-spin block h-5 w-5 rounded-full" style={{ border: '2px solid var(--color-brand-dim)', borderTopColor: 'var(--color-brand)' }} />
          ) : state === 'done' ? (
            <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="var(--color-brand)" strokeWidth="2"><path d="M5 10.5l3.2 3L15 7" /></svg>
          ) : (
            <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke={state === 'armed' ? 'var(--color-brand)' : 'currentColor'} strokeWidth="2"
              style={{ transform: `rotate(${state === 'armed' ? 180 : progress * 150}deg)` }}>
              <path d="M10 4v11M5.5 10.5L10 15l4.5-4.5" />
            </svg>
          )}
          {label && <span className="pr-1">{label}</span>}
        </div>
      </div>
      <p className="sr-only" role="status" aria-live="polite">{state === 'done' ? 'Page updated' : ''}</p>
    </>
  );
}
